// Scene-graph rebuild handlers. Bridge the SCENE_* intent surface to the
// @scene/* builders so orchestrators (datasetManager, theme, sceneIO) can
// dispatch a single intent instead of importing scene modules directly.
// Keeps the intent bus the canonical mutation path; Redux DevTools also gets
// visibility into rebuild events for free.

import { INTENTS } from '@store/intents';
import { params } from '@store/instance';
import { rebuildAllSlices } from '@scene/slices';
import { syncVolume } from '@scene/volume';
import { applyAxisGrid } from '@scene/grid';
import { applyAxesVectors, setAxesVectorsVisible } from '@scene/axes';
import type { Store } from '@store/store';
import type { State } from '@store/schema';
import type { ObjectRegistry } from '../objectRegistry';
import type { ViewerContext } from '../context';

export function register(store: Store<State>, viewer: ViewerContext): Array<() => void> {
    const offs: Array<() => void> = [];

    offs.push(store.onIntent(INTENTS.SCENE_REBUILD_SLICES, () => {
        rebuildAllSlices(viewer, params);
    }));

    offs.push(store.onIntent(INTENTS.SCENE_SYNC_VOLUME, () => {
        syncVolume(viewer, params);
    }));

    offs.push(store.onIntent(INTENTS.SCENE_APPLY_AXIS_GRID, () => {
        applyAxisGrid(viewer, params);
        if (viewer.sceneMeta.showCoordAxes && viewer.sceneMeta.axesVectorsConfig) {
            applyAxesVectors(viewer, viewer.sceneMeta.axesVectorsConfig as unknown as Parameters<typeof applyAxesVectors>[1]);
        }
    }));

    // Layers-panel toggles — own the scene-graph mutation + the params mirror so
    // the panel stays a pure intent dispatcher (see src/ui/README.md boundary).
    offs.push(store.onIntent(INTENTS.SET_AXES_VECTORS_VISIBILITY, (p) => {
        if (!p) return;
        const visible = !!p.visible;
        params.showAxesVectors = visible;
        try { setAxesVectorsVisible(viewer, visible); } catch { /* axes not built */ }
    }));

    offs.push(store.onIntent(INTENTS.SET_BODY_VISIBILITY, (p) => {
        if (!p || typeof p.id !== 'string') return;
        const registry = (viewer.objects.sceneRegistry as unknown as ObjectRegistry | null) ?? null;
        const entry = registry?.listGroup('physics').find((e) => e.id === p.id);
        if (entry) entry.threeObject.visible = !!p.visible;
    }));

    return offs;
}
